import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionAnimation, SectionAnimationPreset } from "@repo/backend/schema/siteConfig";

gsap.registerPlugin(ScrollTrigger);

type ResolvedAnimation = Required<SectionAnimation>;
type PresetFn = (el: HTMLElement, config: ResolvedAnimation) => void;

function staggerTargets(el: HTMLElement): Element[] {
  // 1. Explicit opt-in targets
  const explicit = el.querySelectorAll("[data-stagger-child]");
  if (explicit.length > 0) return Array.from(explicit);

  // 2. Grandchildren — wrapper > section > ul/grid > items
  const firstBlock = el.querySelector("section, [data-section-block]");
  if (firstBlock) {
    const grandkids = Array.from(firstBlock.children).flatMap((c) =>
      c.children.length > 1 ? Array.from(c.children) : [c]
    );
    if (grandkids.length > 1) return grandkids;
  }

  const firstChild = el.firstElementChild;
  if (firstChild && firstChild.children.length > 1) return Array.from(firstChild.children);
  return Array.from(el.children);
}

// Splits text into per-word spans for clip-path word reveals.
// Idempotent — skips if already split (data-split-done attr).
function splitWords(el: HTMLElement): HTMLElement[] {
  if (el.dataset.splitDone) {
    return Array.from(el.querySelectorAll<HTMLElement>(".split-word-inner"));
  }
  const original = el.innerHTML;
  const words = original.split(/(\s+)/);
  el.innerHTML = words
    .map((w) =>
      /^\s+$/.test(w)
        ? w
        : `<span class="split-word-wrap" style="display:inline-block;overflow:hidden;vertical-align:bottom;"><span class="split-word-inner" style="display:inline-block;">${w}</span></span>`
    )
    .join("");
  el.dataset.splitDone = "1";
  return Array.from(el.querySelectorAll<HTMLElement>(".split-word-inner"));
}

const presetMap: Record<Exclude<SectionAnimationPreset, "none">, PresetFn> = {
  // ─── Original presets (enhanced defaults) ────────────────────────────────

  "fade-up": (el, { duration, ease, delay, start, distance, scrub }) => {
    gsap.fromTo(
      el,
      { opacity: 0, y: distance },
      {
        opacity: 1,
        y: 0,
        duration,
        ease,
        delay,
        scrollTrigger: {
          trigger: el,
          start,
          toggleActions: scrub ? undefined : "play none none none",
          scrub: scrub || undefined,
        },
      }
    );
  },

  "fade-in": (el, { duration, ease, delay, start }) => {
    gsap.fromTo(
      el,
      { opacity: 0 },
      {
        opacity: 1,
        duration,
        ease,
        delay,
        scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
      }
    );
  },

  "slide-left": (el, { duration, ease, delay, start, distance }) => {
    gsap.fromTo(
      el,
      { opacity: 0, x: -distance },
      {
        opacity: 1,
        x: 0,
        duration,
        ease,
        delay,
        scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
      }
    );
  },

  "slide-right": (el, { duration, ease, delay, start, distance }) => {
    gsap.fromTo(
      el,
      { opacity: 0, x: distance },
      {
        opacity: 1,
        x: 0,
        duration,
        ease,
        delay,
        scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
      }
    );
  },

  "stagger-children": (el, { duration, ease, delay, start, stagger, distance }) => {
    const targets = staggerTargets(el);
    if (!targets.length) return;
    gsap.fromTo(
      targets,
      { opacity: 0, y: distance * 0.65 },
      {
        opacity: 1,
        y: 0,
        duration,
        ease,
        delay,
        stagger,
        scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
      }
    );
  },

  // Clip-path wipe from bottom → top
  "reveal-clip": (el, { duration, ease, delay, start }) => {
    gsap.fromTo(
      el,
      { clipPath: "inset(0 0 100% 0)" },
      {
        clipPath: "inset(0 0 0% 0)",
        duration: duration * 1.1,
        ease,
        delay,
        scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
      }
    );
  },

  // Parallax background — [data-parallax] child moves slower than scroll
  "parallax-bg": (el) => {
    const target = el.querySelector<HTMLElement>("[data-parallax]");
    if (!target) return;
    gsap.fromTo(
      target,
      { y: -60 },
      {
        y: 60,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5,
        },
      }
    );
  },

  // Stacked-card sticky exit — pins section, next one slides in underneath
  "sticky-exit": (el) => {
    const inner =
      el.querySelector<HTMLElement>("section") ??
      (el.firstElementChild as HTMLElement | null);
    if (!inner) return;

    const windowH = window.innerHeight;
    const panelH = inner.offsetHeight;
    const diff = panelH - windowH;
    const fakeScrollRatio = diff > 0 ? diff / (diff + windowH) : 0;

    if (fakeScrollRatio) {
      gsap.set(el, { marginBottom: panelH * fakeScrollRatio });
    }

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "bottom bottom",
        end: () =>
          fakeScrollRatio ? `+=${inner.offsetHeight}` : "bottom top",
        pinSpacing: false,
        pin: true,
        scrub: true,
      },
    });

    if (fakeScrollRatio) {
      tl.to(inner, {
        yPercent: -100,
        y: windowH,
        duration: 1 / (1 - fakeScrollRatio) - 1,
        ease: "none",
      });
    }

    tl.fromTo(
      el,
      { scale: 1, opacity: 1 },
      { scale: 0.88, opacity: 0.5, duration: 0.9, ease: "none" }
    ).to(el, { opacity: 0, duration: 0.1, ease: "none" });
  },

  // ─── Storytelling presets ─────────────────────────────────────────────────

  // Continuous scrub parallax — section background/content moves at 60% scroll speed.
  // Great for full-bleed hero exits and image-heavy sections.
  "scrub-parallax": (el, { distance }) => {
    gsap.fromTo(
      el,
      { yPercent: -6 },
      {
        yPercent: 6,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        },
      }
    );
    // Also fade out section as user scrolls past the fold
    gsap.fromTo(
      el,
      { opacity: 1 },
      {
        opacity: 0.4,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "center top",
          end: `+=${distance * 3}`,
          scrub: 1.5,
        },
      }
    );
  },

  // Cinematic zoom-out — section starts slightly scaled up, resolves to 1:1.
  // Use on full-bleed sections for a filmic feel.
  "zoom-out": (el, { duration, ease, start }) => {
    gsap.fromTo(
      el,
      { scale: 1.06, opacity: 0 },
      {
        scale: 1,
        opacity: 1,
        duration: duration * 1.3,
        ease,
        scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
      }
    );
  },

  // Dramatic stagger — each child enters with y + slight rotation + scale,
  // creating a cascade effect. Best on card grids and feature lists.
  "stagger-cascade": (el, { duration, ease, delay, start, stagger }) => {
    const targets = staggerTargets(el);
    if (!targets.length) return;
    gsap.fromTo(
      targets,
      { opacity: 0, y: 72, scale: 0.94, rotateX: 8 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        rotateX: 0,
        duration: duration * 1.1,
        ease,
        delay,
        stagger: stagger * 1.4,
        scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
        transformOrigin: "top center",
      }
    );
  },

  // Per-word clip reveal — finds [data-word-reveal] headings and reveals each
  // word by sliding it up out of an overflow:hidden wrapper.
  "word-reveal": (el, { duration, ease, delay, start, stagger }) => {
    const headings = el.querySelectorAll<HTMLElement>("[data-word-reveal]");
    const targets: HTMLElement[] = headings.length
      ? Array.from(headings)
      : Array.from(el.querySelectorAll<HTMLElement>("h1, h2"));

    if (!targets.length) return;

    const allWords: HTMLElement[] = [];
    targets.forEach((h) => allWords.push(...splitWords(h)));

    if (!allWords.length) return;

    gsap.fromTo(
      allWords,
      { yPercent: 110, opacity: 0 },
      {
        yPercent: 0,
        opacity: 1,
        duration: duration * 0.9,
        ease,
        delay,
        stagger: stagger * 0.6,
        scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
      }
    );
  },
};

const DEFAULTS: ResolvedAnimation = {
  preset: "fade-up",
  duration: 0.9,
  delay: 0,
  ease: "power3.out",
  scrub: false,
  start: "top 90%",
  stagger: 0.1,
  distance: 52,
};

export function applyPreset(el: HTMLElement, partial: SectionAnimation): void {
  if (partial.preset === "none") return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const config: ResolvedAnimation = { ...DEFAULTS, ...partial };
  const fn = presetMap[config.preset as Exclude<SectionAnimationPreset, "none">];
  if (fn) fn(el, config);
}
